import { createTheme } from "@mui/material/styles";

// Maison-style design system: ivory paper, ink text, a single champagne
// accent used sparingly, hairline borders and square corners. Display text
// is set in a light serif (Cormorant Garamond), UI text in a quiet geometric
// sans (Jost). Both are loaded in public/index.html.
export const fonts = {
    serif: '"Cormorant Garamond", "Times New Roman", Georgia, serif',
    sans: '"Jost", "Helvetica Neue", Arial, sans-serif',
};

// Hover states stay subtle: a faint wash plus the accent colour, never a
// scale/rotate "bounce".
const hoverAccent = {
    light: { bg: "rgba(28, 27, 25, 0.04)", fg: "#8A7356" },
    dark: { bg: "rgba(237, 233, 227, 0.06)", fg: "#C9B08A" },
};

// Tracked uppercase label used by buttons, nav links and small headings.
const label = {
    fontFamily: fonts.sans,
    fontWeight: 400,
    fontSize: "0.72rem",
    letterSpacing: "0.18em",
    textTransform: "uppercase",
};

export const getTheme = (mode) => {
    const isLight = mode !== "dark";
    const ink = isLight ? "#1C1B19" : "#EDE9E3";
    const paper = isLight ? "#FAF8F5" : "#121211";
    const hairline = isLight ? "#E4DFD8" : "#2E2C29";

    return createTheme({
        cssVariables: true,
        palette: {
            ...(isLight
                ? {
                    // NOTE: primary.main doubles as the page background across
                    // the existing scss, primary.color as the foreground.
                    primary: { main: paper, color: ink, contrastText: ink },
                    secondary: { main: ink },
                    background: { default: paper, paper: "#F2EEE8", footer: "#F2EEE8" },
                    text: { primary: ink, secondary: "#6F6A63" },
                    divider: hairline,
                    footer: {
                        main: "#F2EEE8",
                        paper: "transparent",
                        text: ink,
                        secondary: "#6F6A63",
                        border: hairline,
                    },
                    about: {
                        primary: "#8A7356", secondary: "#1C1B19", accent: "#B89B72",
                        light: "#F2EEE8", dark: "#1C1B19", gradientStart: "#1C1B19", gradientEnd: "#1C1B19",
                    },
                }
                : {
                    primary: { main: paper, color: ink, contrastText: ink },
                    secondary: { main: ink },
                    background: { default: paper, paper: "#1A1918", footer: "#0D0D0C" },
                    text: { primary: ink, secondary: "#A39D94" },
                    divider: hairline,
                    footer: {
                        main: "#0D0D0C",
                        paper: "transparent",
                        text: ink,
                        secondary: "#A39D94",
                        border: hairline,
                    },
                    about: {
                        primary: "#C9B08A", secondary: "#EDE9E3", accent: "#C9B08A",
                        light: "#1A1918", dark: "#EDE9E3", gradientStart: "#EDE9E3", gradientEnd: "#EDE9E3",
                    },
                }),
            mode,
        },
        typography: {
            fontFamily: fonts.sans,
            fontWeightLight: 300,
            fontWeightRegular: 400,
            fontWeightMedium: 500,
            fontWeightBold: 500,
            h1: { fontFamily: fonts.serif, fontWeight: 300, fontSize: "clamp(2.4rem, 4vw, 4.2rem)", letterSpacing: "0.01em", lineHeight: 1.1 },
            h2: { fontFamily: fonts.serif, fontWeight: 300, fontSize: "clamp(2rem, 3.2vw, 3.2rem)", letterSpacing: "0.01em", lineHeight: 1.15 },
            h3: { fontFamily: fonts.serif, fontWeight: 400, fontSize: "clamp(1.6rem, 2.4vw, 2.4rem)", lineHeight: 1.2 },
            h4: { fontFamily: fonts.serif, fontWeight: 400, fontSize: "1.6rem", lineHeight: 1.25 },
            h5: { fontFamily: fonts.serif, fontWeight: 400, fontSize: "1.3rem" },
            h6: { fontFamily: fonts.serif, fontWeight: 500, fontSize: "1.1rem" },
            subtitle1: { fontWeight: 300, letterSpacing: "0.02em" },
            body1: { fontWeight: 300, lineHeight: 1.7 },
            body2: { fontWeight: 300, lineHeight: 1.6 },
            button: label,
            overline: { ...label, fontSize: "0.68rem", letterSpacing: "0.24em" },
            caption: { fontWeight: 300, letterSpacing: "0.04em" },
            // Kept for components that still request the old variant.
            headingStencil: { fontFamily: fonts.serif, fontWeight: 300, fontSize: "clamp(2rem, 3.2vw, 3.2rem)" },
        },
        shape: { borderRadius: 0 },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: { fontFamily: fonts.sans, fontWeight: 300, WebkitFontSmoothing: "antialiased" },
                },
            },
            MuiButtonBase: {
                defaultProps: { disableRipple: true },
                styleOverrides: {
                    root: {
                        "&.Mui-focusVisible": {
                            outline: `1px solid ${ink}`,
                            outlineOffset: "3px",
                        },
                    },
                },
            },
            // primary.main is the page background (see palette note), so a
            // checked primary checkbox/radio would vanish — draw them in ink.
            MuiCheckbox: {
                styleOverrides: {
                    root: { color: ink, "&.Mui-checked": { color: ink } },
                },
            },
            MuiRadio: {
                styleOverrides: {
                    root: { color: ink, "&.Mui-checked": { color: ink } },
                },
            },
            MuiButton: {
                defaultProps: { disableElevation: true },
                styleOverrides: {
                    root: ({ theme }) => ({
                        ...label,
                        color: ink,
                        borderRadius: 0,
                        padding: "10px 18px",
                        minWidth: 0,
                        boxShadow: "none",
                        transition: "color .25s ease, background-color .25s ease, border-color .25s ease",
                        "&:hover": {
                            backgroundColor: "transparent",
                            color: hoverAccent[theme.palette.mode].fg,
                        },
                    }),
                    contained: {
                        backgroundColor: ink,
                        color: paper,
                        padding: "14px 32px",
                        "&:hover": { backgroundColor: isLight ? "#3A3733" : "#FFFFFF", color: paper },
                    },
                    outlined: {
                        border: `1px solid ${ink}`,
                        padding: "13px 31px",
                        "&:hover": { backgroundColor: ink, color: paper, borderColor: ink },
                    },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        color: ink,
                        borderRadius: 0,
                        transition: "color .25s ease",
                        "&:hover": {
                            backgroundColor: "transparent",
                            color: hoverAccent[theme.palette.mode].fg,
                        },
                    }),
                },
            },
            MuiSvgIcon: {
                styleOverrides: {
                    root: { fontSize: "1.25rem" },
                },
            },
            MuiSelect: {
                defaultProps: { variant: "outlined", size: "small" },
                styleOverrides: {
                    root: {
                        ...label,
                        fontSize: "0.68rem",
                        color: ink,
                        borderRadius: 0,
                        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    },
                    select: { paddingLeft: 8, paddingRight: "24px !important" },
                    icon: { color: ink, fontSize: "1rem" },
                },
            },
            MuiMenu: {
                styleOverrides: {
                    paper: {
                        borderRadius: 0,
                        border: `1px solid ${hairline}`,
                        boxShadow: "0 12px 32px rgba(0,0,0,0.06)",
                    },
                },
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: { ...label, fontSize: "0.7rem", minHeight: 36 },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: { borderRadius: 0 },
                    notchedOutline: { borderColor: hairline },
                },
            },
            MuiFormLabel: {
                styleOverrides: {
                    root: { color: isLight ? "#6F6A63" : "#A39D94", fontSize: "0.8rem", letterSpacing: "0.04em" },
                },
            },
            MuiCard: {
                styleOverrides: { root: { borderRadius: 0 } },
            },
            MuiPaper: {
                styleOverrides: {
                    root: { backgroundColor: paper, color: ink, backgroundImage: "none" },
                    rounded: { borderRadius: 0 },
                },
            },
            MuiDrawer: {
                styleOverrides: { paper: { borderRadius: 0 } },
            },
            MuiDialog: {
                styleOverrides: { paper: { borderRadius: 0 } },
            },
            MuiChip: {
                styleOverrides: { root: { borderRadius: 0, ...label, fontSize: "0.64rem" } },
            },
            MuiCircularProgress: {
                defaultProps: { size: 28, thickness: 2 },
                styleOverrides: { root: { color: ink } },
            },
        },
    });
};

export default getTheme;
